import { type ChangeEvent, type FC, useMemo } from "react";
import { SimpleSelect } from "../../../common/components/Select/SimpleSelect";
import { MultilevelSelect, type MultilevelSelectItemItem } from "../../../common/components/Select/MultilevelSelect";
import { groupByFn } from "../../../utils/reducers";
import { useTranslation } from "react-i18next";
import { Box, InputLabel, type SelectChangeEvent } from "@mui/material";

interface Props {
  className?: string;
  selected: string;
  onChange?: (newVal: string | undefined) => void;
  onBlur?: () => void;
  disabled?: boolean;
  highlight?: boolean;
  error?: boolean;
  helperLabel?: string;
  hideLabel?: boolean;
  externalLabel?: boolean;
  size?: "small" | "medium";
}

enum EndCustomerViewType {
  STANDARD = "STANDARD",
  GROUPED = "GROUPED",
  HIDDEN = "HIDDEN",
}

window.SCHA.config.priceReferences[0].value = "-";

const priceReferenceItemCustomerAccountGroup = {
  ZAEC: "partner.type.tilda.one.translation",
  OTHER: "partner.type.zf.translation",
  NO_PRICEREF: "-",
};

const levels: Dict<MultilevelSelectItemItem[]> = groupByFn(window.SCHA.config.priceReferences, (priceReference) => {
  if (priceReference.label === priceReferenceItemCustomerAccountGroup["NO_PRICEREF"]) {
    return priceReferenceItemCustomerAccountGroup["NO_PRICEREF"];
  }
  return priceReference.customerAccountGroup
    ? priceReferenceItemCustomerAccountGroup[priceReference.customerAccountGroup]
      ? priceReferenceItemCustomerAccountGroup[priceReference.customerAccountGroup]
      : priceReferenceItemCustomerAccountGroup["OTHER"]
    : "";
});

const PriceReferenceDropdown: FC<Props> = ({
  onChange,
  onBlur,
  selected,
  hideLabel = false,
  error,
  helperLabel,
  className,
  externalLabel,
  highlight = false,
  size = "small",
  ...props
}) => {
  const { t } = useTranslation();

  const onSelect = (e: SelectChangeEvent<unknown>) => {
    if (onChange) {
      let val = e.target.value as string;
      if (val === "-") {
        val = "";
      }
      onChange(val);
    }
  };
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      let val = e.target.value as string;
      if (val === "-") {
        val = "";
      }
      onChange(val);
    }
  };

  const groupedValues: MultilevelSelectItemItem[] = useMemo(() => {
    return Object.entries(levels).map(([customerAccountGroup, children]) => {
      if (customerAccountGroup === priceReferenceItemCustomerAccountGroup["NO_PRICEREF"]) {
        return {
          label: customerAccountGroup,
          value: customerAccountGroup,
          title: false,
        };
      }
      return {
        label: t(customerAccountGroup),
        value: customerAccountGroup,
        title: true,
        children: children,
      };
    });
  }, [t]);

  const labelText = hideLabel ? undefined : t("component.dropdown.label.priceReference");
  const internalLabelText = externalLabel ? undefined : labelText;

  let cmp;

  const value = selected === "" ? "-" : selected;
  if (window.SCHA.config.endCustomerViewType === EndCustomerViewType.STANDARD) {
    cmp = (
      <SimpleSelect
        label={internalLabelText}
        fullWidth={true}
        values={window.SCHA.config.priceReferences}
        value={value}
        onChange={handleOnChange}
        onBlur={onBlur}
        error={error}
        helperText={helperLabel}
        size={size}
        {...props}
      />
    );
  } else {
    cmp = (
      <MultilevelSelect
        label={internalLabelText}
        onBlur={onBlur}
        onChange={onSelect}
        value={value}
        list={groupedValues}
        variant={"outlined"}
        error={error}
        highlight={highlight}
        helperLabel={helperLabel}
        size={size}
        {...props}
      />
    );
  }

  if (externalLabel && labelText) {
    return (
      <Box>
        <InputLabel shrink>{labelText}</InputLabel>
        {cmp}
      </Box>
    );
  } else {
    return cmp;
  }
};
export default PriceReferenceDropdown;
